// eslint-disable-next-line no-restricted-imports -- getImageSource takes a raw hex, not a $token, so the token bridge PSIcon exists for doesn't apply
import { Ionicons } from '@expo/vector-icons';
import { Stack } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useState } from 'react';
import { Platform, type ImageSourcePropType } from 'react-native';
import type { SFSymbol } from 'sf-symbols-typescript';
import { ScrollView, XStack, YStack } from 'tamagui';

import MembershipPurchaseStatus from '@/components/pages/MembershipPurchaseStatus';
import PSText from '@/components/ui/PSText';
import { color, space } from '@/constants/tokens';
import { PSErrorClass } from '@/src/api/apiError';
import type { Membership } from '@/src/api/membershipsApi';
import {
  TIMEOUT_MESSAGE,
  type PollMembershipState,
} from '@/src/hooks/usePollMembership';
import {
  ACCESS_PASS_PLAN,
  couponMembership,
  heldMembership,
  TRIAL_PLAN,
} from '@/src/lib/membershipFixtures';

/**
 * ── /components/pages/membership-purchase-status ─────────────────────────
 *
 * The purchase status screen, one state at a time — it's a full-height page, so
 * the states are a picker rather than a stack: a native header menu, or a chip
 * row on web, where `Stack.Toolbar` renders nothing. The gallery's header is
 * opaque, hence `headerHeight={0}`.
 * ─────────────────────────────────────────────────────────────────────────
 */

const settled = (
  membership: ReturnType<typeof heldMembership>
): PollMembershipState => ({ status: 'success', membership, error: null });

const failed = (error: PSErrorClass): PollMembershipState => ({
  status: 'error',
  membership: null,
  error,
});

const shareAfter = (ms: number, error?: PSErrorClass) => (_url: string) =>
  new Promise<void>((resolve, reject) =>
    setTimeout(() => (error ? reject(error) : resolve()), ms)
  );

const PREVIEW_STATES: {
  id: string;
  label: string;
  poll: PollMembershipState;
  plan?: Membership;
  canAskForSocialLink?: boolean;
  onShareSocialLink?: (url: string) => Promise<void>;
}[] = [
  {
    id: 'confirming',
    label: 'Confirming',
    poll: { status: 'loading', membership: null, error: null },
    plan: ACCESS_PASS_PLAN,
  },
  {
    id: 'confirming-unnamed',
    label: 'Confirming, plans not loaded',
    poll: { status: 'loading', membership: null, error: null },
  },
  {
    id: 'activated-renews',
    label: 'Activated · renews',
    poll: settled(heldMembership('access_pass', 'Access Pass')),
  },
  {
    id: 'activated-valid-until',
    label: 'Activated · valid until',
    poll: settled(couponMembership('access_pass', 'Access Pass', 30)),
  },
  {
    id: 'activated-socials',
    label: 'Activated · socials ask',
    poll: settled(heldMembership('access_pass', 'Access Pass')),
    canAskForSocialLink: true,
  },
  {
    id: 'activated-socials-rejected',
    label: 'Socials ask · server rejects',
    poll: settled(heldMembership('access_pass', 'Access Pass')),
    canAskForSocialLink: true,
    onShareSocialLink: shareAfter(
      800,
      new PSErrorClass({
        kind: 'api',
        status: 422,
        message: 'Enter a valid URL.',
      })
    ),
  },
  {
    id: 'timed-out',
    label: 'Not confirmed · timed out',
    poll: failed(
      new PSErrorClass({ kind: 'unknown', message: TIMEOUT_MESSAGE })
    ),
    plan: ACCESS_PASS_PLAN,
  },
  {
    id: 'server-verdict',
    label: 'Not confirmed · 4xx',
    poll: failed(
      new PSErrorClass({
        kind: 'api',
        status: 404,
        message: 'Membership not found.',
      })
    ),
    plan: ACCESS_PASS_PLAN,
  },
  {
    id: 'trial-confirming',
    label: 'Trial · confirming',
    poll: { status: 'loading', membership: null, error: null },
    plan: TRIAL_PLAN,
  },
  {
    id: 'trial-activated',
    label: 'Trial · activated',
    poll: settled(couponMembership('trial', 'Trial Pass', 30)),
    canAskForSocialLink: true,
  },
  {
    id: 'trial-timed-out',
    label: 'Trial · not confirmed',
    poll: failed(
      new PSErrorClass({ kind: 'unknown', message: TIMEOUT_MESSAGE })
    ),
    plan: TRIAL_PLAN,
  },
  {
    id: 'settled-not-active',
    label: 'Not confirmed · settled halted',
    poll: settled({
      ...heldMembership('access_pass', 'Access Pass'),
      status: 'halted',
    }),
  },
];

export default function MembershipPurchaseStatusPreview() {
  const [stateId, setStateId] = useState(PREVIEW_STATES[0].id);
  const state =
    PREVIEW_STATES.find((candidate) => candidate.id === stateId) ??
    PREVIEW_STATES[0];
  const menuIcon = useMenuIcon();

  return (
    <YStack flex={1} bg={'$color.background'}>
      <Head>
        <title>Membership purchase status · Putting Scene</title>
      </Head>

      {menuIcon && (
        <Stack.Toolbar placement="right">
          <Stack.Toolbar.Menu icon={menuIcon} title="State">
            {PREVIEW_STATES.map((candidate) => (
              <Stack.Toolbar.MenuAction
                key={candidate.id}
                isOn={candidate.id === state.id}
                onPress={() => setStateId(candidate.id)}
              >
                {candidate.label}
              </Stack.Toolbar.MenuAction>
            ))}
          </Stack.Toolbar.Menu>
        </Stack.Toolbar>
      )}

      {Platform.OS === 'web' && (
        <ScrollView
          horizontal
          flexGrow={0}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ px: space.lg, py: space.sm }}
        >
          <XStack gap={'$space.sm'}>
            {PREVIEW_STATES.map((candidate) => {
              const active = candidate.id === state.id;
              return (
                <YStack
                  key={candidate.id}
                  onPress={() => setStateId(candidate.id)}
                  cursor="pointer"
                  px={'$space.md'}
                  py={'$space.xs'}
                  rounded={'$base'}
                  borderWidth={'$borderWidth.hairline'}
                  borderColor={active ? '$color.textDark' : '$borderColor'}
                >
                  <PSText.Body4
                    color={active ? '$color.textDark' : '$color.textDarkMuted'}
                  >
                    {candidate.label}
                  </PSText.Body4>
                </YStack>
              );
            })}
          </XStack>
        </ScrollView>
      )}

      {/* Keyed so the socials card's local state resets between states. */}
      <MembershipPurchaseStatus
        key={state.id}
        poll={state.poll}
        plan={state.plan}
        canAskForSocialLink={state.canAskForSocialLink ?? false}
        onShareSocialLink={state.onShareSocialLink ?? shareAfter(800)}
        onLeave={() => {}}
        headerHeight={0}
      />
    </YStack>
  );
}

// Android's menu trigger only draws an image source — SF Symbols are dropped.
function useMenuIcon() {
  const [androidIcon, setAndroidIcon] = useState<ImageSourcePropType | null>(
    null
  );
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    Ionicons.getImageSource('options-outline', 24, color.textDark).then(
      (source) => source && setAndroidIcon(source)
    );
  }, []);
  return Platform.select<SFSymbol | ImageSourcePropType | null>({
    ios: 'slider.horizontal.3',
    android: androidIcon,
    default: null,
  });
}
